// SLA clock presentation: remaining time + tone by percentage of the target consumed
// (research 02 §4: warn at 75 / 90 %, breach at 100 %). The API returns the maths
// (GET /tickets/:id/sla → remaining_minutes_now, elapsed_pct); this only renders it.

export interface SlaClock {
  id: string;
  kind: 'response' | 'plan' | 'resolution';
  cycle: number;
  status: 'running' | 'paused' | 'met' | 'breached' | 'cancelled';
  target_minutes: number;
  target_at: string;
  met_at: string | null;
  breached_at: string | null;
  remaining_minutes_now: number | null;
  elapsed_pct: number | null;
}

export type SlaTone = 'pos' | 'warn' | 'neg' | 'muted' | 'info';

export function slaTone(c: Pick<SlaClock, 'status' | 'elapsed_pct'>, warn: number[] = [75, 90]): SlaTone {
  if (c.status === 'met') return 'pos';
  if (c.status === 'breached') return 'neg';
  if (c.status === 'cancelled') return 'muted';
  if (c.status === 'paused') return 'info';
  const pct = c.elapsed_pct ?? 0;
  const [w1 = 75, w2 = 90] = [...warn].sort((a, b) => a - b);
  if (pct >= 100) return 'neg';
  if (pct >= w2) return 'neg';
  if (pct >= w1) return 'warn';
  return 'pos';
}

/** "2h 15m", "35m", "-1h 05m" (overdue), "3d 2h". */
export function formatRemaining(minutes: number | null | undefined): string {
  if (minutes == null) return '—';
  const neg = minutes < 0;
  const m = Math.abs(Math.round(minutes));
  const d = Math.floor(m / 1440);
  const h = Math.floor((m % 1440) / 60);
  const mm = m % 60;
  let s: string;
  if (d > 0) s = `${d}d ${h}h`;
  else if (h > 0) s = `${h}h ${String(mm).padStart(2, '0')}m`;
  else s = `${mm}m`;
  return neg ? `-${s}` : s;
}

export const CLOCK_LABEL: Record<SlaClock['kind'], string> = { response: 'Respond', plan: 'Plan', resolution: 'Resolve' };

export function clockSummary(c: SlaClock): string {
  switch (c.status) {
    case 'met': return `${CLOCK_LABEL[c.kind]} met`;
    case 'breached': return `${CLOCK_LABEL[c.kind]} breached`;
    case 'cancelled': return `${CLOCK_LABEL[c.kind]} cancelled`;
    case 'paused': return `${CLOCK_LABEL[c.kind]} paused · ${formatRemaining(c.remaining_minutes_now)} left`;
    default: return `${CLOCK_LABEL[c.kind]} ${formatRemaining(c.remaining_minutes_now)}`;
  }
}
